import { ChevronDown, ChevronLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, Navigate, NavLink, Outlet, useLocation } from 'react-router-dom'

import { useListNotionUnmatchedApiDoctorsNotionUnmatchedGet } from '../../shared/api/generated/doctors/doctors'
import { SETTINGS_HOME, SETTINGS_SECTIONS, settingsItemFor, type SettingsItem } from './settingsNav'
import './settings.css'

const MOBILE_QUERY = '(max-width: 768px)'

/**
 * Configurações no modelo da Meta: barra de seções (que abrem e fecham) à esquerda e o item escolhido ao lado.
 * No celular é uma escada: a home mostra as seções e cada item tem o caminho de volta.
 */
export function SettingsLayout() {
  const { pathname } = useLocation()
  const current = settingsItemFor(pathname)
  const isHome = pathname === SETTINGS_HOME
  // A seção do item aberto começa aberta e as outras fechadas; o que você abre ou fecha à mão fica como deixou.
  const [toggled, setToggled] = useState<Record<string, boolean>>({})
  const isSectionOpen = (id: string) => toggled[id] ?? id === (current?.section.id ?? SETTINGS_SECTIONS[0].id)
  const toggle = (id: string) => setToggled((prev) => ({ ...prev, [id]: !isSectionOpen(id) }))

  // Pendências mostradas como número na barra (hoje: médicos vistos no Notion sem cadastro).
  const notionUnmatchedQ = useListNotionUnmatchedApiDoctorsNotionUnmatchedGet<unknown[]>({
    query: { staleTime: 5 * 60 * 1000 },
  })
  const badgeOf = (item: SettingsItem) =>
    item.badge === 'notion-unmatched' ? (notionUnmatchedQ.data?.length ?? 0) : 0

  return (
    <div className={`settings-shell ${isHome ? 'settings-shell--home' : ''}`}>
      <div className="settings-nav-col">
        <nav className="settings-nav" aria-label="Seções das configurações">
          {SETTINGS_SECTIONS.map((section) => {
            const isOpen = isSectionOpen(section.id)
            const sectionBadge = section.items.reduce((sum, item) => sum + badgeOf(item), 0)
            return (
              <div key={section.id} className="settings-nav-section">
                <button
                  type="button"
                  className="settings-nav-head"
                  aria-expanded={isOpen}
                  onClick={() => toggle(section.id)}
                >
                  <section.icon size={16} aria-hidden />
                  <span>{section.label}</span>
                  {!isOpen && sectionBadge > 0 && (
                    <span className="settings-nav-badge" aria-label={`${sectionBadge} pendências`}>
                      {sectionBadge}
                    </span>
                  )}
                  <ChevronDown size={14} aria-hidden className={`settings-nav-chevron ${isOpen ? 'open' : ''}`} />
                </button>
                {isOpen && (
                  <div className="settings-nav-items">
                    {section.items.map((item) => {
                      const active = current?.item === item
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          // Função: o destaque vem do dono da URL, não do casamento exato do NavLink.
                          className={() => `settings-nav-item ${active ? 'active' : ''}`}
                          aria-current={active ? 'page' : undefined}
                        >
                          {item.label}
                          {badgeOf(item) > 0 && (
                            <span
                              className="settings-nav-badge"
                              aria-label={`${badgeOf(item)} pendências`}
                            >
                              {badgeOf(item)}
                            </span>
                          )}
                        </NavLink>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </nav>
      </div>
      <div className="settings-content">
        <Link to={SETTINGS_HOME} className="settings-back">
          <ChevronLeft size={14} aria-hidden /> Configurações
        </Link>
        <Outlet />
      </div>
    </div>
  )
}

/** A porta da engrenagem: no computador abre o primeiro item; no celular a própria lista de seções é a tela. */
export function SettingsHome() {
  const [mobile, setMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches)
  useEffect(() => {
    const query = window.matchMedia(MOBILE_QUERY)
    const update = () => setMobile(query.matches)
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])
  if (mobile) return null
  return <Navigate to={SETTINGS_SECTIONS[0].items[0].to} replace />
}
